import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Shield, Leaf, FlaskConical, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useProducts } from '../context/ProductsContext';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 }
};

// Category icons using lucide or SVG paths (no emojis)
const CategoryIcon = ({ type }) => {
  const icons = {
    drinks: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 2h8l-1 7H9L8 2z"/><path d="M9 9c0 5 3 8 3 8s3-3 3-8"/><path d="M6 22h12"/><path d="M12 17v5"/>
      </svg>
    ),
    gummies: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="2"/><rect x="14" y="3" width="7" height="7" rx="2"/><rect x="3" y="14" width="7" height="7" rx="2"/><rect x="14" y="14" width="7" height="7" rx="2"/>
      </svg>
    ),
    flower: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22V12"/><path d="M12 12C12 12 8 9 8 6a4 4 0 0 1 8 0c0 3-4 6-4 6z"/><path d="M12 12c0 0-4 3-7 3"/><path d="M12 12c0 0 4 3 7 3"/>
      </svg>
    ),
    vapes: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="7" y="2" width="10" height="18" rx="3"/><path d="M10 6h4"/><path d="M4 16c0 0 2-2 4 0s4 0 4 0"/>
      </svg>
    ),
    bundles: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 8V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2"/><path d="M3 8h18v13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8z"/><path d="M10 8v8"/><path d="M14 8v8"/>
      </svg>
    ),
  };
  return icons[type] || null;
};

const HomePage = ({ onAddToCart }) => {
  const { products } = useProducts();
  const [activeEffect, setActiveEffect] = useState('All');
  const [sliderIndex, setSliderIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const effects = ['All', 'Chill', 'Creative', 'Energized', 'Sleep'];
  const featured = products.slice(0, 4);
  const sliderProducts = products.slice(0, 5);

  const goNext = useCallback(() => {
    setDirection(1);
    setSliderIndex(i => (i + 1) % sliderProducts.length);
  }, [sliderProducts.length]);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setSliderIndex(i => (i - 1 + sliderProducts.length) % sliderProducts.length);
  }, [sliderProducts.length]);

  useEffect(() => {
    const t = setInterval(goNext, 5000);
    return () => clearInterval(t);
  }, [goNext]);

  const current = sliderProducts[sliderIndex];

  const reviews = [
    { name: 'Sarah M.', rating: 5, text: 'The mango drink is absolutely incredible. Perfect dose, perfect flavor. My new wind-down ritual.', product: 'Mango Bliss Drink', location: 'Austin, TX' },
    { name: 'James K.', rating: 5, text: 'Mind Blown bundles are unreal value. Lab results are right there on the site — transparent and trustworthy.', product: 'Mind Blown Bundle', location: 'Denver, CO' },
    { name: 'Priya L.', rating: 5, text: 'The midnight berry gummies are exactly what I needed. No grogginess, just deep sleep.', product: 'Midnight Berry Gummies', location: 'Portland, OR' },
  ];

  const sliderVariants = {
    enter: (dir) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -80 : 80, opacity: 0 }),
  };

  return (
    <div style={{ paddingTop: 0 }}>

      {/* HERO */}
      <section style={{
        minHeight: '100vh',
        background: 'var(--forest)',
        position: 'relative',
        display: 'flex', alignItems: 'center',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `
            radial-gradient(ellipse at 20% 50%, rgba(74,124,89,0.35) 0%, transparent 60%),
            radial-gradient(ellipse at 80% 20%, rgba(201,168,76,0.2) 0%, transparent 50%)
          `,
        }} />
        {[
          { size: 600, top: '-15%', right: '-10%', opacity: 0.06 },
          { size: 300, bottom: '5%', left: '5%', opacity: 0.08 },
        ].map((circle, i) => (
          <div key={i} style={{
            position: 'absolute',
            width: circle.size, height: circle.size,
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,' + circle.opacity + ')',
            top: circle.top, bottom: circle.bottom,
            left: circle.left, right: circle.right,
          }} />
        ))}

        <div style={{
          maxWidth: '1280px', margin: '0 auto',
          padding: '120px 32px 80px',
          width: '100%', zIndex: 2,
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: '80px', alignItems: 'center',
        }} className="hero-grid">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
          >
            <motion.div variants={fadeUp} style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'rgba(201,168,76,0.15)',
              border: '1px solid rgba(201,168,76,0.3)',
              borderRadius: '4px', padding: '6px 14px',
              marginBottom: '28px',
            }}>
              <span style={{
                fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: 'var(--gold)',
              }}>Premium Hemp-Derived THC</span>
            </motion.div>

            <motion.h1 variants={fadeUp} style={{
              fontFamily: 'Syne', fontWeight: 700,
              fontSize: 'clamp(2.8rem, 5vw, 4.5rem)',
              color: 'var(--white)', lineHeight: '1.05',
              letterSpacing: '-0.01em', marginBottom: '20px',
            }}>
              exquisite<br />
              <span style={{ color: 'var(--gold)' }}>clouds.</span>
            </motion.h1>

            <motion.p variants={fadeUp} style={{
              fontFamily: 'DM Sans', fontStyle: 'italic',
              color: 'rgba(255,255,255,0.55)', fontSize: '1.4rem',
              marginBottom: '32px', fontWeight: 300, letterSpacing: '0.01em',
            }}>
              pure state of mind.
            </motion.p>

            <motion.p variants={fadeUp} style={{
              color: 'rgba(255,255,255,0.65)',
              fontSize: '1rem', lineHeight: '1.8',
              maxWidth: '420px', marginBottom: '40px',
            }}>
              Curated hemp-derived THC drinks, gummies, flower & vapes.
              Every product third-party lab tested. Every batch COA verified.
            </motion.p>

            <motion.div variants={fadeUp} style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
              <Link to="/shop" style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                background: 'var(--gold)',
                color: 'var(--white)',
                borderRadius: '6px', padding: '16px 32px',
                fontFamily: 'Syne', fontWeight: 700, fontSize: '0.9rem',
                letterSpacing: '0.05em', textTransform: 'uppercase',
                transition: 'all 0.25s', textDecoration: 'none',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--gold-light)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--gold)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                Shop Now <ArrowRight size={16} />
              </Link>
              <Link to="/build-box" style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                background: 'transparent',
                color: 'var(--white)',
                border: '1.5px solid rgba(255,255,255,0.3)',
                borderRadius: '6px', padding: '16px 32px',
                fontFamily: 'Syne', fontWeight: 600, fontSize: '0.9rem',
                letterSpacing: '0.05em', textTransform: 'uppercase',
                transition: 'all 0.25s', textDecoration: 'none',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.7)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}>
                Build a Box
              </Link>
            </motion.div>
          </motion.div>

          {/* PRODUCT SLIDER */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            style={{ position: 'relative' }}
            className="hero-slider"
          >
            <div style={{
              borderRadius: '20px',
              overflow: 'hidden',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              position: 'relative',
              minHeight: '440px',
            }}>
              <AnimatePresence custom={direction} mode="wait">
                <motion.div
                  key={sliderIndex}
                  custom={direction}
                  variants={sliderVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                  style={{ position: 'absolute', inset: 0 }}
                >
                  {/* Product image */}
                  <img
                    src={current.image}
                    alt={current.name}
                    style={{
                      width: '100%', height: '100%',
                      objectFit: 'cover', objectPosition: 'center',
                    }}
                  />
                  {/* Gradient overlay */}
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(to top, rgba(10,30,18,0.85) 0%, rgba(10,30,18,0.1) 55%, transparent 100%)',
                  }} />
                  {/* Product info overlay */}
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    padding: '28px 28px 24px',
                  }}>
                    <div style={{
                      display: 'flex', gap: '8px', marginBottom: '10px',
                      alignItems: 'center',
                    }}>
                      <span style={{
                        fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em',
                        textTransform: 'uppercase', color: 'var(--gold)',
                        background: 'rgba(201,168,76,0.2)', borderRadius: '4px',
                        padding: '3px 8px', border: '1px solid rgba(201,168,76,0.3)',
                      }}>{current.effect}</span>
                      <span style={{
                        fontSize: '0.65rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)',
                        textTransform: 'uppercase', letterSpacing: '0.08em',
                      }}>{current.mg}</span>
                    </div>
                    <h3 style={{
                      fontFamily: 'Syne', fontWeight: 700, fontSize: '1.4rem',
                      color: 'var(--white)', letterSpacing: '-0.01em', marginBottom: '6px',
                    }}>{current.name}</h3>
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                      <span style={{
                        fontFamily: 'Syne', fontWeight: 700, fontSize: '1.2rem',
                        color: 'var(--gold)',
                      }}>${current.price}</span>
                      <Link to={`/product/${current.id}`} style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        background: 'var(--gold)', color: 'var(--white)',
                        padding: '9px 18px', borderRadius: '6px',
                        fontFamily: 'Syne', fontWeight: 700, fontSize: '0.75rem',
                        letterSpacing: '0.06em', textTransform: 'uppercase',
                        textDecoration: 'none', transition: 'background 0.2s',
                      }}>
                        View Product <ArrowRight size={13} />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Arrow controls */}
              <button onClick={goPrev} style={{
                position: 'absolute', left: '14px', top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '50%', width: '42px', height: '42px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'var(--white)',
                backdropFilter: 'blur(8px)',
                transition: 'background 0.2s', zIndex: 10,
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}>
                <ChevronLeft size={18} />
              </button>
              <button onClick={goNext} style={{
                position: 'absolute', right: '14px', top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '50%', width: '42px', height: '42px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'var(--white)',
                backdropFilter: 'blur(8px)',
                transition: 'background 0.2s', zIndex: 10,
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}>
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Dot indicators */}
            <div style={{
              display: 'flex', gap: '6px', justifyContent: 'center',
              marginTop: '16px',
            }}>
              {sliderProducts.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setDirection(i > sliderIndex ? 1 : -1); setSliderIndex(i); }}
                  style={{
                    width: i === sliderIndex ? '24px' : '6px',
                    height: '6px',
                    borderRadius: '3px',
                    background: i === sliderIndex ? 'var(--gold)' : 'rgba(255,255,255,0.25)',
                    border: 'none', cursor: 'pointer',
                    transition: 'all 0.3s ease', padding: 0,
                  }}
                />
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          style={{
            position: 'absolute', bottom: '32px', left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: '6px', color: 'rgba(255,255,255,0.4)',
            fontSize: '0.68rem', letterSpacing: '0.12em', textTransform: 'uppercase',
          }}
        >
          <span>Scroll</span>
          <div style={{
            width: '1px', height: '36px',
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.4), transparent)',
          }} />
        </motion.div>
      </section>

      {/* TRUST BAR */}
      <section style={{
        background: 'var(--smoke)',
        padding: '18px 32px',
        borderBottom: '1px solid var(--mist)',
      }}>
        <div style={{
          maxWidth: '1280px', margin: '0 auto',
          display: 'flex', justifyContent: 'center',
          flexWrap: 'wrap', gap: '28px',
        }}>
          {[
            { icon: <Leaf size={14} />, text: '100% Organic Hemp' },
            { icon: <FlaskConical size={14} />, text: 'Third-Party Lab Tested' },
            { icon: <Shield size={14} />, text: 'CGMP Certified' },
            { icon: <Shield size={14} />, text: 'Age Verified Checkout' },
          ].map(item => (
            <div key={item.text} style={{
              display: 'flex', alignItems: 'center', gap: '7px',
              color: 'var(--mid)',
            }}>
              <span style={{ color: 'var(--accent-green)' }}>{item.icon}</span>
              <span style={{
                fontFamily: 'DM Sans', fontWeight: 500, fontSize: '0.8rem',
                color: 'var(--mid)', letterSpacing: '0.01em',
              }}>{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section style={{ padding: '80px 32px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <motion.div
            initial="hidden" whileInView="visible"
            viewport={{ once: true }} variants={fadeUp}
            style={{ textAlign: 'center', marginBottom: '52px' }}
          >
            <span style={{
              fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: 'var(--gold)',
              display: 'block', marginBottom: '12px',
            }}>Explore</span>
            <h2 style={{
              fontFamily: 'Syne', fontWeight: 700,
              fontSize: 'clamp(2rem, 3.5vw, 3rem)',
              color: 'var(--forest)', letterSpacing: '-0.01em',
            }}>Shop by Category</h2>
          </motion.div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '16px',
          }} className="cat-grid">
            {[
              { name: 'THC Drinks', type: 'drinks', gradient: 'linear-gradient(135deg, #fff9e6, #ffe4a0)', path: '/shop?cat=drinks', count: 12 },
              { name: 'Gummies', type: 'gummies', gradient: 'linear-gradient(135deg, #f3e8ff, #c8a8f0)', path: '/shop?cat=gummies', count: 18 },
              { name: 'Flower', type: 'flower', gradient: 'linear-gradient(135deg, #e8f5e9, #a8d5b5)', path: '/shop?cat=flower', count: 8 },
              { name: 'Vapes', type: 'vapes', gradient: 'linear-gradient(135deg, #e8f0ff, #a8c0f0)', path: '/shop?cat=vapes', count: 14 },
              { name: 'Bundles', type: 'bundles', gradient: 'linear-gradient(135deg, #fdf0e0, #d4a870)', path: '/shop?cat=bundles', count: 6 },
            ].map((cat, i) => (
              <motion.div
                key={cat.name}
                initial="hidden" whileInView="visible"
                viewport={{ once: true }}
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { delay: i * 0.08 } } }}
              >
                <Link to={cat.path} style={{
                  display: 'block',
                  background: cat.gradient,
                  borderRadius: '16px', padding: '32px 20px',
                  textAlign: 'center', textDecoration: 'none',
                  border: '1px solid var(--mist)',
                  transition: 'all 0.25s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-hover)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                  <div style={{
                    width: '52px', height: '52px',
                    background: 'rgba(0,0,0,0.08)',
                    borderRadius: '12px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 14px', color: 'var(--charcoal)',
                  }}>
                    <CategoryIcon type={cat.type} />
                  </div>
                  <div style={{
                    fontFamily: 'Syne', fontWeight: 700, fontSize: '0.92rem',
                    color: 'var(--charcoal)', marginBottom: '4px',
                  }}>{cat.name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--mid)' }}>{cat.count} products</div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section style={{ padding: '40px 32px 80px', background: 'var(--smoke)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{
            display: 'flex', alignItems: 'flex-end',
            justifyContent: 'space-between', marginBottom: '40px',
            flexWrap: 'wrap', gap: '20px',
          }}>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <span style={{
                fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: 'var(--gold)',
                display: 'block', marginBottom: '10px',
              }}>Fan Favorites</span>
              <h2 style={{
                fontFamily: 'Syne', fontWeight: 700,
                fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
                color: 'var(--forest)', letterSpacing: '-0.01em',
              }}>Best Sellers</h2>
            </motion.div>
            <Link to="/shop" style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              fontFamily: 'Syne', fontWeight: 700, fontSize: '0.82rem',
              color: 'var(--forest)', letterSpacing: '0.05em',
              textTransform: 'uppercase', textDecoration: 'none',
              borderBottom: '1.5px solid var(--forest)', paddingBottom: '2px',
            }}>
              View All <ArrowRight size={14} />
            </Link>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '20px',
          }} className="products-grid">
            {featured.map((product, i) => (
              <motion.div
                key={product.id}
                initial="hidden" whileInView="visible"
                viewport={{ once: true }}
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { delay: i * 0.1 } } }}
              >
                <ProductCard product={product} onAddToCart={onAddToCart} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* EFFECT GUIDE */}
      <section style={{ padding: '80px 32px', background: 'var(--white)' }}>
        <div style={{
          maxWidth: '1280px', margin: '0 auto',
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: '80px', alignItems: 'center',
        }} className="two-col">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span style={{
              fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: 'var(--gold)',
              display: 'block', marginBottom: '12px',
            }}>Find Your Vibe</span>
            <h2 style={{
              fontFamily: 'Syne', fontWeight: 700,
              fontSize: 'clamp(1.8rem, 3vw, 2.8rem)',
              color: 'var(--forest)', letterSpacing: '-0.01em',
              marginBottom: '20px', lineHeight: '1.1',
            }}>
              Shop by Effect,<br />
              <span style={{ color: 'var(--gold)' }}>Not Guesswork.</span>
            </h2>
            <p style={{
              color: 'var(--mid)', lineHeight: '1.8',
              fontSize: '0.95rem', marginBottom: '32px',
            }}>
              Every product is tagged by expected effect and onset time.
              No lab degree required — just tell us how you want to feel.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '36px' }}>
              {effects.map(effect => (
                <button
                  key={effect}
                  onClick={() => setActiveEffect(effect)}
                  style={{
                    padding: '9px 18px', borderRadius: '6px',
                    background: activeEffect === effect ? 'var(--forest)' : 'var(--smoke)',
                    color: activeEffect === effect ? 'var(--white)' : 'var(--mid)',
                    border: activeEffect === effect ? 'none' : '1.5px solid var(--mist)',
                    fontFamily: 'Syne', fontWeight: 600, fontSize: '0.8rem',
                    letterSpacing: '0.04em', cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {effect}
                </button>
              ))}
            </div>

            <Link to={`/shop${activeEffect !== 'All' ? `?effect=${activeEffect}` : ''}`} style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'var(--forest)', color: 'var(--white)',
              borderRadius: '6px', padding: '14px 28px',
              fontFamily: 'Syne', fontWeight: 700, fontSize: '0.85rem',
              letterSpacing: '0.05em', textTransform: 'uppercase',
              textDecoration: 'none', transition: 'all 0.25s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-green)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--forest)'}>
              {activeEffect === 'All' ? 'Shop All' : `Shop ${activeEffect}`} <ArrowRight size={15} />
            </Link>
          </motion.div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            {[
              { effect: 'Chill', color: '#4a7c59', bg: '#e8f5e9', desc: 'Relax and unwind without the couch-lock.' },
              { effect: 'Creative', color: '#e0953a', bg: '#fff6e0', desc: 'Flow state for artists and thinkers.' },
              { effect: 'Energized', color: '#e05c3a', bg: '#fff0ec', desc: 'Social energy, elevated focus.' },
              { effect: 'Sleep', color: '#5a6fa0', bg: '#f0e8ff', desc: 'Deep, restful, natural sleep.' },
            ].map((item, i) => (
              <motion.div
                key={item.effect}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                style={{
                  background: item.bg,
                  borderRadius: '16px', padding: '28px 22px',
                  border: '1px solid var(--mist)',
                }}
              >
                <div style={{
                  width: '40px', height: '40px', borderRadius: '10px',
                  background: item.color + '22',
                  marginBottom: '14px',
                }} />
                <div style={{
                  fontFamily: 'Syne', fontWeight: 700, fontSize: '1rem',
                  color: item.color, marginBottom: '6px',
                }}>{item.effect}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--mid)', lineHeight: '1.5' }}>
                  {item.desc}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BUILD A BOX CTA */}
      <section style={{ padding: '0 32px 80px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <motion.div
            initial="hidden" whileInView="visible"
            viewport={{ once: true }} variants={fadeUp}
            style={{
              background: 'linear-gradient(135deg, var(--forest) 0%, #2d5a3e 100%)',
              borderRadius: '20px', padding: '64px 60px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '40px', flexWrap: 'wrap', position: 'relative', overflow: 'hidden',
            }}
          >
            <div style={{
              position: 'absolute', right: '-80px', top: '-80px',
              width: '360px', height: '360px', borderRadius: '50%',
              border: '1px solid rgba(255,255,255,0.06)',
            }} />

            <div style={{ zIndex: 2 }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                background: 'rgba(201,168,76,0.2)',
                border: '1px solid rgba(201,168,76,0.35)',
                borderRadius: '4px', padding: '5px 12px', marginBottom: '20px',
              }}>
                <span style={{
                  fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em',
                  textTransform: 'uppercase', color: 'var(--gold)',
                }}>Save 15%</span>
              </div>
              <h2 style={{
                fontFamily: 'Syne', fontWeight: 700,
                fontSize: 'clamp(1.8rem, 3vw, 2.6rem)',
                color: 'var(--white)', letterSpacing: '-0.01em',
                marginBottom: '14px', lineHeight: '1.1',
              }}>
                Build Your Own<br />
                <span style={{ color: 'var(--gold)' }}>Mind Blown Box</span>
              </h2>
              <p style={{
                color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem', lineHeight: '1.7',
                maxWidth: '400px',
              }}>
                Mix and match drinks, gummies & flower. Get 15% off every custom box,
                plus free shipping on orders over $75.
              </p>
            </div>

            <Link to="/build-box" style={{
              display: 'inline-flex', alignItems: 'center', gap: '12px',
              background: 'var(--gold)', color: 'var(--white)',
              borderRadius: '6px', padding: '18px 36px',
              fontFamily: 'Syne', fontWeight: 700, fontSize: '0.95rem',
              letterSpacing: '0.05em', textTransform: 'uppercase',
              textDecoration: 'none', zIndex: 2, whiteSpace: 'nowrap',
              transition: 'all 0.25s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--gold-light)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--gold)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              Start Building <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* REVIEWS */}
      <section style={{ padding: '0 32px 80px', background: 'var(--smoke)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', paddingTop: '80px' }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            style={{ textAlign: 'center', marginBottom: '52px' }}>
            <span style={{
              fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: 'var(--gold)',
              display: 'block', marginBottom: '12px',
            }}>Verified Reviews</span>
            <h2 style={{
              fontFamily: 'Syne', fontWeight: 700,
              fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
              color: 'var(--forest)', letterSpacing: '-0.01em',
            }}>Real People, Real Results</h2>
          </motion.div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px',
          }} className="reviews-grid">
            {reviews.map((review, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                style={{
                  background: 'var(--white)',
                  borderRadius: '16px', padding: '32px',
                  border: '1px solid var(--mist)',
                }}
              >
                <div style={{ display: 'flex', gap: '3px', marginBottom: '16px' }}>
                  {[...Array(review.rating)].map((_, j) => (
                    <Star key={j} size={13} fill="var(--gold)" color="var(--gold)" />
                  ))}
                </div>
                <p style={{
                  fontSize: '0.9rem', color: 'var(--charcoal)', lineHeight: '1.7',
                  marginBottom: '20px', fontStyle: 'italic',
                }}>"{review.text}"</p>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <div>
                    <div style={{
                      fontFamily: 'Syne', fontWeight: 700, fontSize: '0.85rem',
                      color: 'var(--charcoal)',
                    }}>{review.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--mid)' }}>{review.location}</div>
                  </div>
                  <span style={{
                    fontSize: '0.7rem', color: 'var(--accent-green)',
                    background: '#e8f5e9', borderRadius: '4px',
                    padding: '4px 10px', fontWeight: 600,
                  }}>Verified</span>
                </div>
                <div style={{
                  marginTop: '14px', paddingTop: '14px',
                  borderTop: '1px solid var(--mist)',
                  fontSize: '0.73rem', color: 'var(--mid)',
                }}>
                  Purchased: {review.product}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY MIND BLOWN */}
      <section style={{ padding: '80px 32px', background: 'var(--white)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', textAlign: 'center' }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            style={{ marginBottom: '52px' }}>
            <span style={{
              fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: 'var(--gold)',
              display: 'block', marginBottom: '12px',
            }}>The Difference</span>
            <h2 style={{
              fontFamily: 'Syne', fontWeight: 700,
              fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
              color: 'var(--forest)', letterSpacing: '-0.01em',
            }}>Why Mind Blown?</h2>
          </motion.div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '32px',
          }} className="why-grid">
            {[
              { icon: <Shield size={26} />, title: 'Fully Compliant', desc: 'Age-verified checkout, state-by-state compliance, and transparent lab results on every single product.', color: '#4a7c59' },
              { icon: <FlaskConical size={26} />, title: 'Lab Verified', desc: 'Every batch has a Certificate of Analysis from an accredited third-party lab. Scan the QR on packaging anytime.', color: '#c9a84c' },
              { icon: <Leaf size={26} />, title: 'Curated Quality', desc: 'We source only from CGMP-certified partner facilities. If we would not consume it ourselves, it does not ship.', color: '#1a3a2a' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                style={{
                  padding: '40px 32px',
                  borderRadius: '20px',
                  border: '1px solid var(--mist)',
                  background: 'var(--smoke)',
                  textAlign: 'left',
                }}
              >
                <div style={{
                  width: '52px', height: '52px',
                  background: item.color + '18',
                  borderRadius: '12px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: item.color, marginBottom: '20px',
                }}>{item.icon}</div>
                <h3 style={{
                  fontFamily: 'Syne', fontWeight: 700, fontSize: '1.1rem',
                  color: 'var(--charcoal)', marginBottom: '10px', letterSpacing: '-0.02em',
                }}>{item.title}</h3>
                <p style={{ fontSize: '0.88rem', color: 'var(--mid)', lineHeight: '1.75' }}>
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 1100px) {
          .hero-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .hero-slider { max-width: 560px; }
          .cat-grid { grid-template-columns: repeat(3, 1fr) !important; }
          .products-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 760px) {
          .cat-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .products-grid { grid-template-columns: 1fr !important; }
          .two-col { grid-template-columns: 1fr !important; }
          .reviews-grid { grid-template-columns: 1fr !important; }
          .why-grid { grid-template-columns: 1fr !important; }
          .hero-slider { min-height: 320px !important; }
        }
      `}</style>
    </div>
  );
};

export default HomePage;